import { useState, useRef } from 'react';
import { View, Text, Pressable, Alert, ScrollView, Platform } from 'react-native';
import { Link, useRouter } from 'expo-router';
import { useAuthStore } from '../../store/authStore';
import { authApi } from '../../shared/utils/api';
import { useStyles } from '../../hooks/useStyles';
import { Input, PasswordInput, Button, Card } from '../../components/ui';
import { addColorOpacity } from '../../shared/utils/styles';
import { useResponsive, getResponsiveValue } from '../../shared/utils/responsive';
import { Ionicons } from '@expo/vector-icons';
import Animated, { 
  useSharedValue, 
  useAnimatedStyle, 
  withSpring,
  FadeInUp,
} from 'react-native-reanimated';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const setAuth = useAuthStore((state) => state.setAuth);
  const styles = useStyles();
  const { tokens, utils } = styles;
  const responsive = useResponsive();
  const { isMobile, isTablet } = responsive;
  const passwordInputRef = useRef<any>(null);

  // Button animation
  const buttonScale = useSharedValue(1);
  const buttonAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: buttonScale.value }],
  }));

  const handleButtonPressIn = () => {
    buttonScale.value = withSpring(0.96, { damping: 15, stiffness: 300 });
  };

  const handleButtonPressOut = () => {
    buttonScale.value = withSpring(1, { damping: 15, stiffness: 300 });
  };

  const handleLogin = async () => {
    setError(null);
    
    if (!email && !password) {
      setError('Please fill in all fields');
      return;
    }
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    try {
      const trimmedEmail = email.trim();
      const response = await authApi.login({ email: trimmedEmail, password });
      
      // On web, token is in httpOnly cookie (not in response for security)
      // On mobile, token is in response
      const token = response.token || (Platform.OS === 'web' ? 'cookie' : null);
      
      if (!response.user.role) {
        try {
          const meResponse = await authApi.getMe();
          setAuth(meResponse.user, token || 'cookie');
        } catch (meError) {
          setAuth({ ...response.user, role: 'user' }, token || 'cookie');
        }
      } else {
        setAuth(response.user, token || 'cookie');
      }
      router.replace('/v2/home');
    } catch (error: any) {
      let errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      
      if (error.message) {
        if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch') || error.message.includes('CORS')) {
          errorMessage = 'Unable to connect to the server. Please check your connection and try again.';
        } else if (error.message.includes('locked') || error.message.includes('Account temporarily locked')) {
          const retryMatch = error.message.match(/(\d+)\s*minute/);
          if (retryMatch) {
            errorMessage = `Account temporarily locked due to too many failed login attempts. Please try again in ${retryMatch[1]} minute(s).`;
          } else {
            errorMessage = 'Account temporarily locked. Please try again later.';
          }
        } else if (error.message.includes('rate limit') || error.message.includes('too many requests')) {
          errorMessage = 'Too many login attempts. Please wait a few minutes before trying again.';
        } else if (error.message.includes('Invalid credentials') || error.message.includes('Invalid email or password')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        }
      }
      
      setError(errorMessage);
      Alert.alert('Login Failed', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Responsive values
  const containerMaxWidth = getResponsiveValue(440, 500, 500, 520);
  const containerPadding = getResponsiveValue(
    utils.spacing.md,
    utils.spacing.lg,
    utils.spacing.xl,
    utils.spacing.xl
  );
  const headingSize = getResponsiveValue(28, 30, 32, 32);
  const subtitleSize = getResponsiveValue(16, 17, 18, 18);
  const bodySize = getResponsiveValue(15, 15, 16, 16);
  const buttonSize = getResponsiveValue(16, 17, 18, 18);

  return (
    <View style={[
      styles.container,
      {
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        ...Platform.select({
          web: {
            minHeight: '100vh',
            overflowX: 'hidden',
          } as any,
        }),
      },
    ]}>
      <ScrollView 
        contentContainerStyle={{
          flexGrow: 1,
          width: '100%',
          maxWidth: '100%',
          paddingHorizontal: containerPadding,
          paddingVertical: getResponsiveValue(
            utils.spacing.xl,
            utils.spacing['2xl'],
            utils.spacing['3xl'],
            utils.spacing['3xl']
          ),
          ...Platform.select({
            web: {
              minHeight: '100vh',
              overflowX: 'hidden',
            } as any,
          }),
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        style={{
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <View style={{ 
          width: '100%', 
          maxWidth: containerMaxWidth,
          alignSelf: 'center',
          flex: 1,
          justifyContent: 'center',
          paddingVertical: getResponsiveValue(
            utils.spacing.lg,
            utils.spacing.xl,
            utils.spacing.xl,
            utils.spacing.xl
          ),
        }}>
          {/* Brand Header */}
          <Animated.View 
            entering={FadeInUp.delay(100).springify()}
            style={{ 
              marginBottom: utils.spacing['2xl'], 
              alignItems: 'center',
            }}
          >
            <View style={{
              width: getResponsiveValue(80, 90, 100, 100),
              height: getResponsiveValue(80, 90, 100, 100),
              borderRadius: utils.radius.xl,
              backgroundColor: tokens.color.primary,
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: utils.spacing.lg,
              ...Platform.select({
                web: {
                  boxShadow: `0 8px 32px ${addColorOpacity(tokens.color.primary, 0.25)}`,
                } as any,
                default: {
                  shadowColor: tokens.color.primary,
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.25,
                  shadowRadius: 32,
                  elevation: 8,
                },
              }),
            }}>
              <Ionicons 
                name="leaf" 
                size={getResponsiveValue(40, 44, 48, 48)} 
                color={tokens.color.onPrimary} 
              />
            </View>
            <Text style={[
              {
                fontSize: headingSize,
                fontWeight: utils.typography.weights.bold,
                marginBottom: utils.spacing.xs,
                textAlign: 'center',
                letterSpacing: -0.5,
                color: tokens.color.onBackground,
                fontFamily: utils.typography.heading.fontFamily,
                lineHeight: headingSize * 1.2,
                width: '100%',
              }
            ]}>
              Welcome back
            </Text>
            <Text style={[
              {
                fontSize: subtitleSize,
                color: tokens.color.onBackground,
                textAlign: 'center',
                fontFamily: utils.typography.body.fontFamily,
                lineHeight: subtitleSize * 1.5,
                width: '100%',
                paddingHorizontal: utils.spacing.sm,
              }
            ]}>
              Sign in to continue to Farmer John
            </Text>
          </Animated.View>

          {/* Login Card */}
          <Animated.View
            entering={FadeInUp.delay(200).springify()}
          >
            <Card 
              style={{ 
                width: '100%', 
                marginBottom: utils.spacing.xl,
                ...Platform.select({
                  web: {
                    boxShadow: `0 4px 16px ${addColorOpacity('#000', 0.08)}`,
                    boxSizing: 'border-box',
                  } as any,
                  default: {
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.08,
                    shadowRadius: 16,
                    elevation: 4,
                  },
                }),
              }}
              elevated
            >
              <View style={{ marginBottom: utils.spacing.xl }}>
                <Text style={[
                  {
                    fontSize: getResponsiveValue(20, 21, 22, 22),
                    marginBottom: utils.spacing.xs,
                    color: tokens.color.onSurface,
                    fontWeight: utils.typography.weights.bold,
                    fontFamily: utils.typography.heading.fontFamily,
                    lineHeight: getResponsiveValue(26, 27, 28, 28),
                  }
                ]}>
                  Sign in to your account
                </Text>
                <Text style={[
                  {
                    fontSize: bodySize,
                    color: tokens.color.onBackground,
                    lineHeight: bodySize * 1.5,
                    fontFamily: utils.typography.body.fontFamily,
                  }
                ]}>
                  Enter your email and password to access your account
                </Text>
              </View>

              <View style={styles.form}>
                <Input
                  label="Email address"
                  placeholder="you@example.com"
                  value={email}
                  onChangeText={(text) => {
                    setEmail(text);
                    if (error) setError(null);
                  }}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  autoComplete="email"
                  textContentType="emailAddress"
                  spellCheck={false}
                  autoFocus={!isMobile}
                  returnKeyType="next"
                  error={error && error.includes('email') && !error.includes('password') && !error.includes('credentials') ? error : undefined}
                  onSubmitEditing={() => {
                    passwordInputRef.current?.focus();
                  }}
                />
                <View>
                  <PasswordInput
                    ref={passwordInputRef}
                    label="Password"
                    placeholder="Enter your password"
                    value={password}
                    onChangeText={(text) => {
                      setPassword(text);
                      if (error) setError(null);
                    }}
                    returnKeyType="done"
                    error={error && (!error.includes('email') || error.includes('password') || error.includes('credentials') || error.includes('locked') || error.includes('attempts') || error.includes('connect')) ? error : undefined}
                    onSubmitEditing={handleLogin}
                  />
                </View>
                <Animated.View style={[buttonAnimatedStyle, { width: '100%' }]}>
                  <Pressable
                    onPressIn={handleButtonPressIn}
                    onPressOut={handleButtonPressOut}
                    onPress={handleLogin}
                    disabled={loading}
                    style={({ pressed }) => [
                      {
                        backgroundColor: tokens.color.primary,
                        minHeight: getResponsiveValue(48, 50, 52, 52),
                        paddingHorizontal: utils.spacing.xl,
                        paddingVertical: utils.spacing.md,
                        borderRadius: utils.radius.md,
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: utils.spacing.sm,
                        width: '100%',
                        opacity: loading ? 0.7 : 1,
                        ...Platform.select({
                          web: {
                            boxShadow: `0 6px 20px ${addColorOpacity(tokens.color.primary, 0.35)}`,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            ':hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 28px ${addColorOpacity(tokens.color.primary, 0.45)}`,
                            },
                            ':active': {
                              transform: 'translateY(0px) scale(0.98)',
                            },
                          } as any,
                          default: {
                            shadowColor: tokens.color.primary,
                            shadowOffset: { width: 0, height: 6 },
                            shadowOpacity: 0.35,
                            shadowRadius: 20,
                            elevation: 8,
                            opacity: pressed || loading ? 0.9 : 1,
                          },
                        }),
                      },
                    ]}
                  >
                    <Text style={{
                      fontSize: buttonSize,
                      fontWeight: utils.typography.weights.semibold,
                      letterSpacing: 0.2,
                      color: tokens.color.onPrimary,
                      fontFamily: utils.typography.body.fontFamily,
                    }}>
                      {loading ? 'Signing in...' : 'Sign in'}
                    </Text>
                  </Pressable>
                </Animated.View>
              </View>
            </Card>
          </Animated.View>

          {/* Sign up link */}
          <Animated.View 
            entering={FadeInUp.delay(300).springify()}
            style={{ 
              flexDirection: 'row', 
              alignItems: 'center',
              justifyContent: 'center',
              gap: utils.spacing.xs,
              marginTop: utils.spacing.lg,
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <Text style={[
              {
                fontSize: bodySize,
                color: tokens.color.onBackground,
                fontFamily: utils.typography.body.fontFamily,
                lineHeight: bodySize * 1.5,
              }
            ]}>
              Don't have an account?
            </Text>
            <Link href="/(auth)/signup" asChild>
              <Pressable>
                <Text style={[
                  {
                    fontSize: bodySize,
                    color: tokens.color.primary,
                    fontWeight: utils.typography.weights.semibold,
                    fontFamily: utils.typography.body.fontFamily,
                  }
                ]}>
                  Request access
                </Text>
              </Pressable>
            </Link>
          </Animated.View>
        </View>
      </ScrollView>
    </View>
  );
}
